import React, {FC, useMemo} from 'react';
import Helmet from 'react-helmet';
import HelpdeskLandingPageUI from '../../ui/Helpdesk/HelpdeskLandingPage/HelpdeskLandingPageUI';
import {ClientSideNavigationProvider} from '../../ui/Navigation/ClientSideLink/ClientSideLink';
import {MAYA_ARTICLE_ID} from './constants';
import {filterHiddenSections} from './hiddenSections';
import {isHelpCenterSectionAllowed} from './hooks/useRedirectVipOnlySection';
import {HelpCenterLandingPageData, HelpCenterPageProps} from './types';

const HelpCenterLandingPage: FC<
  HelpCenterPageProps<
    HelpCenterLandingPageData & {
      isShowOldHelpCenterEnabled: boolean;
    }
  >
> = ({
  sections: propsSections = [],
  highlightQuestions: propsHighlightQuestions = [],
  highlightArticles: propsHighlightArticles = [],
  isVip,
  translations,
  alternateLink,
  canonicalUrl,
  isShowOldHelpCenterEnabled,
  navigateClient,
  headerImageLarge,
  locale,
  isMobileHeaderShown,
  headerImageSmall,
  endpoints,
  showLeftArrow,
  hasDroppedSubdomain,
}) => {
  const {sections, highlightQuestions, highlightArticles} = useMemo(() => {
    const isAllowed = isHelpCenterSectionAllowed(isVip);
    return {
      sections: propsSections.filter(isAllowed).filter(filterHiddenSections),
      highlightQuestions: propsHighlightQuestions
        .filter(
          question =>
            isAllowed(question.section) &&
            filterHiddenSections(question.section),
        )
        .map(question => {
          question.highlightQuestions = question.highlightQuestions.filter(
            article => article.id !== MAYA_ARTICLE_ID,
          );

          return question;
        }),
      highlightArticles: propsHighlightArticles.filter(
        article =>
          isAllowed(article.section) &&
          filterHiddenSections(article.section) &&
          article.article.id !== MAYA_ARTICLE_ID,
      ),
    };
  }, [isVip, propsSections.map(section => section.id).join(',')]);

  return (
    <>
      <Helmet
        title={translations.landingPage.seo_page_title}
        meta={[
          {
            name: 'description',
            content: translations.landingPage.seo_page_description,
          },
          {name: 'robots', content: 'index, follow'},
        ]}
        link={[
          {rel: 'canonical', href: canonicalUrl},
          ...(alternateLink && !hasDroppedSubdomain
            ? [
                {
                  rel: 'alternate',
                  ...alternateLink,
                },
              ]
            : []),
        ]}
      />
      <ClientSideNavigationProvider navigateClient={navigateClient}>
        <HelpdeskLandingPageUI
          isShowOldHelpCenterEnabled={isShowOldHelpCenterEnabled}
          locale={locale}
          highlightQuestions={highlightQuestions}
          frequentQuestions={highlightArticles}
          sections={sections}
          translations={translations}
          headerImageLarge={headerImageLarge}
          headerImageSmall={headerImageSmall}
          endpoints={endpoints}
          isMobileHeaderShown={isMobileHeaderShown}
          showLeftArrow={showLeftArrow}
        />
      </ClientSideNavigationProvider>
    </>
  );
};

export default HelpCenterLandingPage;
